import type { ChangeEvent } from 'react';
import type { CurrentUser } from '@hotjar/user';
import { useCurrentUser } from '@hotjar/user';
import type { UseFormMethods } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { bus, profileUpdated } from '@hotjar/webapp/utils/events';
import { restDefaultHandlers } from '@hotjar/webapp/utils/rest-default-handlers';
import { useEffect, useState } from 'react';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { AxiosError } from 'axios';
import { roles } from './roles';
import { getAvatarPreview, saveProfile } from '../../../domains/profile/api';
import type { ProfileDetail } from '../../../domains/profile/types';

type FormData = ProfileDetail;

type FormType = UseFormMethods<FormData>;

export const useAccountDetailForm = (): {
  user: CurrentUser | undefined;
  register: FormType['register'];
  handleSubmit: FormType['handleSubmit'];
  errors: FormType['errors'];
  isDirty: boolean;
  watch: FormType['watch'];
  onSave: (formData: FormData) => void;
  onChangePicture: (event: ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  isCurrentUserLoading: boolean;
} => {
  const { t } = useTranslation('profile');
  const { data: user, refetch, isLoading: isCurrentUserLoading } = useCurrentUser();
  const [isLoading, setLoadingState] = useState(false);
  const { showToast } = useNotify();

  const getDefaultValues = (data: CurrentUser | undefined) => {
    const isRoleInDefaultList = roles.findIndex((role) => role.id === data?.role) > -1;
    return {
      email: data?.email ?? undefined,
      picture:
        data?.avatar !== undefined && data?.avatar !== null
          ? `${data.avatar}?t=${new Date().getTime()}`
          : undefined,
      name: data?.name,
      role: isRoleInDefaultList ? data?.role : 'other',
    };
  };

  const {
    register,
    handleSubmit,
    errors,
    watch,
    setValue,
    setError,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm<FormData>({
    mode: 'onBlur',
  });

  useEffect(() => {
    const values = getDefaultValues(user);
    reset(values);
  }, [reset, user]);

  const onSave = async (formData: FormData) => {
    if (user) {
      setLoadingState(true);

      try {
        const saveResult = await saveProfile(user.id, {
          name: formData.name,
          role: formData.role,
          picture: dirtyFields.picture === true ? formData.picture : undefined,
          email: formData.email,
        });

        const { data: latestUserData } = await refetch();
        const values = getDefaultValues(latestUserData);
        if (latestUserData) {
          bus.publish(
            profileUpdated({
              userName: latestUserData.name,
              userEmail: latestUserData.email,
              userPictureUrl: values.picture,
              userRole: latestUserData.role,
            }),
          );
        }

        if (saveResult.success) {
          showToast({ kind: 'success', text: t<string>('account-detail.success-message') });
        } else {
          showToast({ kind: 'danger', text: t<string>('account-detail.error-message') });
        }
      } catch (e) {
        const requestError = e as AxiosError;
        if (requestError?.response) {
          restDefaultHandlers.onError(requestError.response);
        }
      } finally {
        setLoadingState(false);
      }
    }
  };

  const onChangePicture = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result as string | null;

        if (user?.id !== undefined && base64Image !== null) {
          getAvatarPreview(user.id, base64Image).then((response) => {
            if (response.success) {
              setValue('picture', response.image, { shouldDirty: true });
            } else {
              setError('picture', { message: response.error });
            }
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };
  return {
    user,
    register,
    handleSubmit,
    errors,
    isDirty,
    watch,
    onSave,
    onChangePicture,
    isLoading,
    isCurrentUserLoading,
  };
};
